import { useMemo } from 'react';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography } from '@components/Typography';
import Doughnut from '@components/Charts/Doughnut';
import { ConversationMap } from '@constants/conversations';
import { useTranslation } from '@hooks';
import { formatNumber, formatPercentNumber } from '@helpers/formatNumbers';

const createChartData = (data, hasData, isMobile) => {
    const EMPTY_SPACE = '                                   ';
    const backgroundColors = ['#D0D0D0', '#F3F7D7', '#E7EEB3', '#62828D', '#BDE0CC', '#98CBAD', '#1F4C5C'];
    const borderColors = ['#D0D0D0', '#F3F7D7', '#E7EEB3', '#62828D', '#BDE0CC', '#98CBAD', '#1F4C5C'];
    return {
        labels: Object.entries(ConversationMap).map(([key, value]) => {
            let set;
            if (value === ConversationMap.Archived) {
                set = data.find(d => d.channelType === null);
            } else {
                set = data.find(d => d.channelType === +value);
            }
            return `${key} - ${formatNumber(set?.count || 0)} (${formatPercentNumber(set?.percentage || 0)})${
                isMobile ? EMPTY_SPACE : ''
            }`;
        }),
        datasets: [
            {
                label: '',
                data: !hasData
                    ? [1]
                    : Object.entries(ConversationMap).map(([, value]) => {
                          if (value === ConversationMap.Archived) {
                              return data.find(d => d.channelType === null)?.count || 0;
                          } else {
                              return data.find(d => d.channelType === +value)?.count || 0;
                          }
                      }),
                backgroundColor: !hasData ? ['#F2F2F2', ...backgroundColors] : backgroundColors,
                hoverBackgroundColor: !hasData ? ['#F2F2F2', ...backgroundColors] : backgroundColors,
                borderColor: !hasData ? ['#F2F2F2', ...borderColors] : borderColors,
                hoverBorderColor: !hasData ? ['#F2F2F2', ...borderColors] : borderColors,
                borderWidth: 1,
            },
        ],
    };
};

const ChannelsBreakdown = ({ data, isLoading }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('@media (max-width: 600px)');

    const hasData = useMemo(() => data.channelsBreakdown.some(item => !!item.count), [data]);

    const datasets = useMemo(
        () => createChartData(data.channelsBreakdown, hasData, isMobile),
        [data.channelsBreakdown, hasData, isMobile],
    );

    return (
        <Paper
            elevation={1}
            sx={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                borderRadius: '8px',
                breakInside: 'avoid',
                gridArea: 'doughnut',
            }}
        >
            <Typography variant="title1" style={{ paddingBottom: '10px' }}>
                {t('AnalyticsConversations.channelsBreakdown')}
            </Typography>
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    ...(isMobile
                        ? { height: '460px', margin: '32px 0 0' }
                        : {
                              flex: '1 1 auto',
                              minHeight: '240px',
                              maxHeight: '340px',
                              maxWidth: '640px',
                              alignSelf: 'center',
                          }),
                }}
            >
                {!isLoading ? (
                    <Doughnut
                        datasets={datasets}
                        hasData={hasData}
                        textCenterProps={{
                            fontTitle: '500 13px Rubik, sans-serif',
                            fontData: '500 34px Rubik, sans-serif',
                            yTitleOffset: 12,
                            yDataOffset: 22,
                        }}
                        optionsProps={{
                            plugins: {
                                legend: {
                                    position: isMobile ? 'bottom' : 'right',
                                    ...(isMobile && { align: 'start' }),
                                },
                            },
                        }}
                        overall={{
                            title: t('AnalyticsConversations.totalConversations'),
                            data: data.conversationsCount?.toLocaleString('en-US'),
                        }}
                    />
                ) : null}
            </div>
        </Paper>
    );
};

export default ChannelsBreakdown;
